import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/Modal";
import api from "../../utils/api";
import { getAuthData } from "../../utils/auth";
import "./Disputes.css";

interface ViolationResponse {
  violationTrackerId: string;
  plateNumber: string;
  lotId: string;
  sessionId: string | null;
  vehicleId: string | null;
  violationType: string;
  violationTimestamp: string;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  totalViolationAmount: string | number | null;
  violationStatus: string;
  evidenceURL: string | null;
  recordedBy: string | null;
  recordedOn: string | null;
  initialNotes: string | null;
  resolvedBy: string | null;
  resolvedOn: string | null;
  resolutionNotes: string | null;
  dismissedBy: string | null;
  dismissedOn: string | null;
  dismissalNotes: string | null;
  disputedBy: string | null;
  disputedOn: string | null;
  disputeNotes: string | null;
}

interface GenericResponse<T> {
  success: boolean;
  data: T;
  message: string;
  status: number;
}

interface ParsedDisputeNotes {
  name: string | null;
  email: string | null;
  reason: string | null;
}

type SortDirection = "ascending" | "descending";
type SortKey =
  | "plateNumber"
  | "violationType"
  | "violationTimestamp"
  | "vehicle"
  | "totalViolationAmount"
  | "disputedOn";

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

const parseDisputeNotes = (notes: string | null): ParsedDisputeNotes => {
  if (!notes) {
    return { name: null, email: null, reason: null };
  }

  const normalized = notes.replace(/\r?\n/g, " ");
  const nameMatch = normalized.match(/Name:\s*([^,]+)/i);
  const emailMatch = normalized.match(/Email:\s*([^,]+)/i);
  const reasonMatch = normalized.match(/Reason:\s*(.*)/i);

  const name = nameMatch ? nameMatch[1].trim() : null;
  const email = emailMatch ? emailMatch[1].trim() : null;
  const reason = reasonMatch ? reasonMatch[1].trim() : null;

  return {
    name: name && name.length > 0 ? name : null,
    email: email && email.length > 0 ? email : null,
    reason: reason && reason.length > 0 ? reason : null,
  };
};

const Disputes: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [disputes, setDisputes] = useState<ViolationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDispute, setSelectedDispute] = useState<ViolationResponse | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"ACCEPT" | "DENY" | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "disputedOn",
    direction: "descending",
  });
  const [isMobile, setIsMobile] = useState(false);

  const loadDisputedViolations = useCallback(async (activeLotId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<GenericResponse<ViolationResponse[]>>(
        "/violations/get-disputed-violations-by-lot",
        { params: { lotId: activeLotId } }
      );
      const responseData = response.data;
      if (responseData.success && Array.isArray(responseData.data)) {
        setDisputes(responseData.data);
      } else {
        setDisputes([]);
      }
    } catch (fetchError) {
      console.error("Failed to load disputed violations", fetchError);
      setError("Unable to load disputed violations. Please try again later.");
      setDisputes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!lotId) {
      return;
    }
    loadDisputedViolations(lotId);
  }, [lotId, loadDisputedViolations]);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === "undefined") {
        setIsMobile(false);
        return;
      }
      setIsMobile(window.innerWidth <= 640);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const filteredDisputes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return disputes;
    }

    return disputes.filter((dispute) =>
      [
        dispute.plateNumber,
        dispute.violationType,
        dispute.violationTimestamp,
        dispute.vehicleMake,
        dispute.vehicleModel,
        dispute.vehicleColor,
      ]
        .filter(Boolean)
        .some((value) => value!.toString().toLowerCase().includes(query))
    );
  }, [disputes, searchQuery]);

  const handleRowClick = (dispute: ViolationResponse) => {
    setSelectedDispute(dispute);
    setActionError(null);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "ascending" ? "descending" : "ascending",
        };
      }
      const defaultDirection = key === "violationTimestamp" || key === "disputedOn" ? "descending" : "ascending";
      return { key, direction: defaultDirection };
    });
  };

  const closeModal = () => setSelectedDispute(null);

  const formatTimestamp = (value: string | null) => {
    if (!value) {
      return "—";
    }
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  const formatViolationType = (value: string) =>
    value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const formatAmount = (value: ViolationResponse["totalViolationAmount"]) => {
    if (value === null || value === undefined) {
      return "—";
    }
    const numericValue = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(numericValue)) {
      return "—";
    }
    return `$${numericValue.toFixed(2)}`;
  };

  const getVehicleDescription = (dispute: ViolationResponse) =>
    [dispute.vehicleColor, dispute.vehicleMake, dispute.vehicleModel].filter(Boolean).join(" ");

  const renderEvidenceLinks = (value: string | null) => {
    if (!value) {
      return <span className="detail-value">No evidence provided.</span>;
    }
    const urls = value.split(/\s+/).filter(Boolean);
    if (!urls.length) {
      return <span className="detail-value">No evidence provided.</span>;
    }
    return (
      <div className="detail-value dispute-evidence-links">
        {urls.map((url, index) => (
          <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
            View evidence {urls.length > 1 ? index + 1 : ""}
          </a>
        ))}
      </div>
    );
  };

  const renderSortIcon = (key: SortKey) => (
    <img
      src={
        sortConfig.key === key
          ? "/assets/tables/SortArrowSelected.svg"
          : "/assets/tables/SortArrow.svg"
      }
      alt=""
      className={`sort-arrow ${sortConfig.key === key && sortConfig.direction === "descending" ? "desc" : ""}`}
    />
  );

  const sortedDisputes = useMemo(() => {
    const data = [...filteredDisputes];
    return data.sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (sortConfig.key) {
        case "violationTimestamp":
          aValue = new Date(a.violationTimestamp).getTime();
          bValue = new Date(b.violationTimestamp).getTime();
          break;
        case "disputedOn":
          aValue = a.disputedOn ? new Date(a.disputedOn).getTime() : 0;
          bValue = b.disputedOn ? new Date(b.disputedOn).getTime() : 0;
          break;
        case "totalViolationAmount":
          aValue =
            typeof a.totalViolationAmount === "number"
              ? a.totalViolationAmount
              : Number(a.totalViolationAmount ?? -1);
          bValue =
            typeof b.totalViolationAmount === "number"
              ? b.totalViolationAmount
              : Number(b.totalViolationAmount ?? -1);
          break;
        case "vehicle":
          aValue = getVehicleDescription(a).toLowerCase();
          bValue = getVehicleDescription(b).toLowerCase();
          break;
        case "violationType":
          aValue = formatViolationType(a.violationType);
          bValue = formatViolationType(b.violationType);
          break;
        case "plateNumber":
        default:
          aValue = (a as any)[sortConfig.key] ?? "";
          bValue = (b as any)[sortConfig.key] ?? "";
          break;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredDisputes, sortConfig]);

  const getUpdatedBy = () => {
    const authData = getAuthData();
    return authData?.userId || "superadmin";
  };

  const handleStatusChange = async (status: "RESOLVED" | "OPEN") => {
    if (!selectedDispute) {
      return;
    }

    setIsUpdatingStatus(true);
    setActionError(null);
    setPendingAction(status === "RESOLVED" ? "ACCEPT" : "DENY");

    const params = new URLSearchParams({
      violationId: selectedDispute.violationTrackerId,
      updatedBy: getUpdatedBy(),
      violationStatus: status,
    });

    try {
      await api.patch(`/violations/update-single-violation?${params.toString()}`);
      setSelectedDispute(null);
      if (lotId) {
        await loadDisputedViolations(lotId);
      }
    } catch (updateError: any) {
      console.error("Failed to update disputed violation status", updateError);
      const message =
        updateError?.response?.data?.message || "Unable to update dispute. Please try again later.";
      setActionError(message);
    } finally {
      setIsUpdatingStatus(false);
      setPendingAction(null);
    }
  };

  const parsedDisputeNotes = useMemo(() => {
    if (!selectedDispute) {
      return null;
    }
    return parseDisputeNotes(selectedDispute.disputeNotes);
  }, [selectedDispute]);

  return (
    <div className="disputes-page">
      <div className="disputes-header">
        <h1>Disputes</h1>
      </div>

      <div className="disputes-search">
        <div className="search-bar">
          <img src="/assets/SearchBarIcon.svg" alt="Search" />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search by plate, type, or vehicle details"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      {sortedDisputes.length > 0 && (
        <div className="table-container app-table-secondary-hover disputes-table-container">
          <table className="app-table disputes-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("plateNumber")}>
                  Plate
                  {renderSortIcon("plateNumber")}
                </th>
                <th onClick={() => handleSort("violationType")}>
                  {isMobile ? "Type" : "Violation Type"}
                  {renderSortIcon("violationType")}
                </th>
                <th onClick={() => handleSort("violationTimestamp")}>
                  {isMobile ? "Time" : "Violation Time"}
                  {renderSortIcon("violationTimestamp")}
                </th>
                <th className="disputes-mobile-hidden" onClick={() => handleSort("vehicle")}>
                  Vehicle
                  {renderSortIcon("vehicle")}
                </th>
                <th className="disputes-mobile-hidden" onClick={() => handleSort("totalViolationAmount")}>
                  Amount
                  {renderSortIcon("totalViolationAmount")}
                </th>
                <th className="disputes-mobile-hidden" onClick={() => handleSort("disputedOn")}>
                  Disputed On
                  {renderSortIcon("disputedOn")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDisputes.map((dispute) => (
                <tr
                  key={dispute.violationTrackerId}
                  className="disputes-row"
                  onClick={() => handleRowClick(dispute)}
                >
                  <td>
                    <span className="plate-badge">{dispute.plateNumber}</span>
                  </td>
                  <td>{formatViolationType(dispute.violationType)}</td>
                  <td>{formatTimestamp(dispute.violationTimestamp)}</td>
                  <td className="disputes-mobile-hidden">
                    {getVehicleDescription(dispute) || "—"}
                  </td>
                  <td className="disputes-mobile-hidden">{formatAmount(dispute.totalViolationAmount)}</td>
                  <td className="disputes-mobile-hidden">{formatTimestamp(dispute.disputedOn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isLoading && (
        <div className="disputes-loading">Loading disputed violations…</div>
      )}

      {!isLoading && error && (
        <div className="empty-state-message">
          <h3>Unable to load disputed violations.</h3>
          <p>{error}</p>
        </div>
      )}

      {!isLoading && !error && filteredDisputes.length === 0 && (
        <div className="empty-state-message">
          <p>No Disputes Found</p>
        </div>
      )}

      <Modal
        isOpen={Boolean(selectedDispute)}
        title=""
        hideButtons
        onCancel={closeModal}
        isVideoModal
        customContent={
          selectedDispute && (
            <div className="dispute-detail">
              <div className="dispute-detail-columns">
                <div className="dispute-column">
                  <div className="dispute-column-top">
                    <span className="plate-badge">{selectedDispute.plateNumber}</span>
                    <span className="amount-value">
                      {formatAmount(selectedDispute.totalViolationAmount)}
                    </span>
                  </div>

                  <h3>Violation</h3>
                  <div className="detail-row">
                    <span className="detail-label">Time</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedDispute.violationTimestamp)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type</span>
                    <span className="detail-value">
                      {formatViolationType(selectedDispute.violationType)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Vehicle</span>
                    <span className="detail-value">
                      {getVehicleDescription(selectedDispute) || "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Recorded By</span>
                    <span className="detail-value">{selectedDispute.recordedBy || "—"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Recorded On</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedDispute.recordedOn)}
                    </span>
                  </div>
                  <div className="detail-note-block">
                    <span className="detail-label">Initial Notes</span>
                    <p className="detail-value multiline">
                      {selectedDispute.initialNotes || "—"}
                    </p>
                  </div>
                </div>

                <div className="dispute-column">
                  <div className="dispute-actions column-actions top-actions">
                    <button
                      type="button"
                      className="button-primary"
                      onClick={() => handleStatusChange("RESOLVED")}
                      disabled={isUpdatingStatus}
                    >
                      {pendingAction === "ACCEPT" && isUpdatingStatus ? "Updating..." : "Accept"}
                    </button>
                    <button
                      type="button"
                      className="button-danger"
                      onClick={() => handleStatusChange("OPEN")}
                      disabled={isUpdatingStatus}
                    >
                      {pendingAction === "DENY" && isUpdatingStatus ? "Updating..." : "Deny"}
                    </button>
                  </div>
                  <h3>Dispute</h3>
                  {actionError && <div className="dispute-error">{actionError}</div>}
                  <div className="detail-row">
                    <span className="detail-label">Disputed</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedDispute.disputedOn)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">
                      {parsedDisputeNotes?.name || selectedDispute.disputedBy || "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{parsedDisputeNotes?.email || "—"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User ID</span>
                    <span className="detail-value">{selectedDispute.disputedBy || "—"}</span>
                  </div>
                  <div className="detail-note-block">
                    <span className="detail-label">Reason</span>
                    <p className="detail-value multiline">
                      {parsedDisputeNotes?.reason || selectedDispute.disputeNotes || "—"}
                    </p>
                  </div>
                  <div className="detail-note-block">
                    <span className="detail-label">Evidence</span>
                    {renderEvidenceLinks(selectedDispute.evidenceURL)}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      />
    </div>
  );
};

export default Disputes;
